import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface SanskritTermProps {
  term: string;
  devanagari: string;
  pronunciation: string;
  meaning: string;
  lore?: string;
}

export function SanskritTerm({
  term,
  devanagari,
  pronunciation,
  meaning,
  lore,
}: SanskritTermProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [position, setPosition] = useState<"above" | "below">("above");
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const pos = spaceAbove < 380 ? "below" : "above";
      setPosition(pos);
      setCoords({
        top:
          pos === "above"
            ? rect.top + window.scrollY - 12
            : rect.bottom + window.scrollY + 12,
        left: rect.left + rect.width / 2,
      });
    }
  }, [isOpen]);

  return (
    <span className="relative inline-block overflow-visible">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="sanskrit-term group relative cursor-pointer text-gold transition-colors duration-300 hover:text-warm-brown"
      >
        <span className="font-serif relative font-medium italic">{term}</span>
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-[100] bg-warm-brown/5 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                className="absolute z-[101] w-80 rounded-md border border-(--color-hairline-strong) bg-warm-white p-6 shadow-[0_12px_40px_rgba(31,29,26,0.14)]"
                style={{
                  top: coords.top,
                  left: coords.left,
                  transform: `translateX(-50%) ${position === "above" ? "translateY(-100%)" : "translateY(0)"}`,
                }}
                initial={{
                  opacity: 0,
                  y: position === "above" ? 8 : -8,
                }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: position === "above" ? 8 : -8,
                }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              >
                {/* Dictionary entry — headword, devanagari, pronunciation,
                    sense, then etymology-style note on a hairline. */}
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-serif text-xl font-semibold text-warm-brown">
                    {term}
                  </span>
                  <span className="font-devanagari text-2xl leading-none text-gold">
                    {devanagari}
                  </span>
                </div>
                <div className="mt-1 text-xs tracking-[0.14em] text-warm-gray uppercase">
                  {pronunciation}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-warm-brown-light">
                  {meaning}
                </p>
                {lore && (
                  <p className="hairline-t mt-4 pt-3 text-xs leading-relaxed text-warm-gray italic">
                    {lore}
                  </p>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </span>
  );
}
